import React from 'react';
import { AlertCircle } from 'lucide-react';
import { Card } from '../ui/Card';
import CompanyInfoCards from './CompanyInfoCards';
import { StockCategoryFilter } from '../../types/stockCategories';
import { StockData } from '../../types/game';
import { cn } from '../../utils/cn';

interface GameHeaderProps {
  category?: StockCategoryFilter;
  currentCategory: StockCategoryFilter;
  stockData?: StockData | null;
  error?: string | null;
  className?: string;
  layout?: 'mobile' | 'desktop';
}

/**
 * GameHeader component - handles the top section of the game
 * Includes intro, category description, and company info cards
 */
export const GameHeader: React.FC<GameHeaderProps> = ({
  category,
  currentCategory,
  stockData,
  error,
  className,
  layout = 'desktop'
}) => {
  const activeCategory = category ?? currentCategory;

  // Target SEO keyword on the primary landing category (popular)
  const isWordleForStocksPage = activeCategory === 'popular';

  const keywordHeading = isWordleForStocksPage ? (
    <h2 className="sr-only">
      Wallstreetle is the Stock Wordle - a daily ticker puzzle powered by real market data and helpful hints.
    </h2>
  ) : null;

  const hasVisibleContent = Boolean(error);

  // On desktop with no error, only render the sr-only heading
  // to avoid an empty flex child that creates a gap in the parent layout
  if (!hasVisibleContent) {
    return <>{keywordHeading}</>;
  }

  return (
    <div className={cn('space-y-2', className)}>
      {keywordHeading}

      {/* Error message */}
      {error && (
        <Card className="p-4 border-destructive bg-destructive/10">
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle size={16} />
            <span className="text-sm font-medium">{error}</span>
          </div>
        </Card>
      )}

      {/* Company info cards */}
      <CompanyInfoCards
        sector={stockData?.overview?.Sector || 'FINANCIAL SERVICES'}
        category={activeCategory}
        showCategoryBadge={false}
      />
    </div>
  );
};
